import React from "react";
import { Link } from "@chakra-ui/react";
import { renderTextWithLinks } from "./linkify";

// A mention is "@" plus up to six words. The picker inserts "@First Last " and
// the member keeps typing, so the capture runs past the name on purpose and
// the member list decides where the name ends. Words stop at whitespace and
// the punctuation that usually follows a name: "@Riccardo Saracino (HEG)".
const WORD = "[^\\s@.,;:!?()\\[\\]{}<>\"/]+";
const MENTION_RE = new RegExp(`(^|[^\\w@])@(${WORD}(?:[ \\t]+${WORD}){0,5})`, "g");

const normalise = (s) => String(s || "").replace(/\s+/g, " ").trim().toLowerCase();

/**
 * Members keyed by their normalised full name, for the lookups below.
 * Built once per member list; see FeedList.
 */
export const buildMemberIndex = (members) => {
  const index = new Map();
  for (const m of members || []) {
    const key = normalise(m?.name);
    if (key && !index.has(key)) index.set(key, m);
  }
  return index;
};

// How many characters of `captured` the first `count` words span.
const spanOfWords = (captured, count) => {
  const re = /\S+/g;
  let end = 0;
  for (let i = 0; i < count; i += 1) {
    const m = re.exec(captured);
    if (!m) break;
    end = m.index + m[0].length;
  }
  return end;
};

// The longest prefix of the captured words that is a member's full name.
const matchMember = (captured, index) => {
  const words = captured.split(/\s+/);
  for (let n = words.length; n >= 1; n -= 1) {
    const member = index.get(normalise(words.slice(0, n).join(" ")));
    if (member) return { member, length: spanOfWords(captured, n) };
  }
  return null;
};

const splitMentions = (text, index, onOpen, keyPrefix) => {
  const out = [];
  let last = 0;
  let m;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text))) {
    const hit = matchMember(m[2], index);
    if (!hit) continue;
    const start = m.index + m[1].length;
    const end = start + 1 + hit.length;
    out.push(text.slice(last, start));
    out.push(
      <Link
        key={`${keyPrefix}-${start}`}
        as="span"
        color="gwbi.600"
        fontWeight="semibold"
        cursor="pointer"
        _hover={{ textDecoration: "underline" }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onOpen) onOpen(hit.member, e);
        }}
      >
        {text.slice(start, end)}
      </Link>
    );
    last = end;
    MENTION_RE.lastIndex = end;
  }
  out.push(text.slice(last));
  return out;
};

/**
 * A post or comment body with its URLs turned into links and its @mentions
 * into links to the member named, resolved against `index` from
 * buildMemberIndex. A name that matches nobody stays plain text. Returns
 * nodes, never HTML, for the same reason renderTextWithLinks does.
 */
export function renderTextWithMentions(text, index, onOpen) {
  const withLinks = renderTextWithLinks(text);
  if (!index || index.size === 0) return withLinks;
  const parts = Array.isArray(withLinks) ? withLinks : [withLinks];
  return parts.flatMap((part, i) =>
    typeof part === "string" ? splitMentions(part, index, onOpen, i) : part
  );
}

export default renderTextWithMentions;
